import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api, { money, fmtDate } from "@/lib/api";
import { Page, PageHeader } from "@/components/Bits";
import { toast } from "sonner";
import { FilePdf, CalendarBlank, MagnifyingGlass, X, MicrosoftExcelLogo, Lock, ArrowSquareOut, CaretRight } from "@phosphor-icons/react";
import { ColumnsToggle, useColumnVisibility } from "@/components/ColumnsToggle";
import MonthPicker from "@/components/MonthPicker";

const TYPE_LABELS = { PREPAYMENT: "Prepayments", ACCRUAL: "Accruals", DEFERRED_INCOME: "Deferred Income", DEPRECIATION: "Depreciation" };
const VALID_TYPES = ["PREPAYMENT", "ACCRUAL", "DEFERRED_INCOME", "DEPRECIATION"];
const TypeLock = ({ label }) => (
  <span data-testid="type-lock-badge" className="inline-flex items-center gap-1.5 text-xs font-semibold text-orangeink bg-orange/10 border border-orange/30 px-2.5 py-1 rounded-lg">
    <Lock size={12} weight="fill" /> {label} only
  </span>
);
const FC_COLUMNS = [
  { key: "orig_date", label: "Original Date" },
  { key: "bs_code", label: "BS Code" },
  { key: "bs_account", label: "BS Account" },
  { key: "pl_code", label: "P&L Code" },
  { key: "pl_account", label: "P&L Account" },
  { key: "orig_amount", label: "Original Amt" },
];
const FC_DEFAULTS = { orig_date: true, bs_code: true, bs_account: true, pl_code: true, pl_account: true, orig_amount: true };
const endOfMonth = () => { const d = new Date(); const e = new Date(d.getFullYear(), d.getMonth() + 1, 0); return `${e.getFullYear()}-${String(e.getMonth() + 1).padStart(2, "0")}-${String(e.getDate()).padStart(2, "0")}`; };

export default function Forecast() {
  const [params] = useSearchParams();
  const lockedType = VALID_TYPES.includes(params.get("type")) ? params.get("type") : null;
  const typeQ = lockedType ? `&type=${lockedType}` : "";
  const [start, setStart] = useState(endOfMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfing, setPdfing] = useState(false);
  const [exceling, setExceling] = useState(false);
  const [search, setSearch] = useState("");
  const [viewType, setViewType] = useState("ALL");
  const [cols, toggleCol] = useColumnVisibility("forecast-columns", FC_DEFAULTS);
  const [mode, setMode] = useState("forecast");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/forecast?start=${start}${typeQ}`);
      setData(data);
    } finally { setLoading(false); }
  }, [start, typeQ]);

  useEffect(() => { load(); }, [load]);

  if (mode === "balance") return <BalanceAtView onBack={() => setMode("forecast")} lockedType={lockedType} />;

  const downloadPdf = async () => {
    setPdfing(true);
    try {
      const res = await api.get(`/forecast/pdf?start=${start}${typeQ}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `release-forecast-${start}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Forecast PDF downloaded");
    } catch { toast.error("Could not generate PDF"); }
    finally { setPdfing(false); }
  };

  const downloadExcel = async () => {
    setExceling(true);
    try {
      const res = await api.get(`/forecast/excel?start=${start}${typeQ}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url; a.download = `release-forecast-${start}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Forecast Excel downloaded");
    } catch { toast.error("Could not generate Excel"); }
    finally { setExceling(false); }
  };

  const months = data?.months || [];
  const ccy = data?.currency || "GBP";
  const q = search.trim().toLowerCase();
  const allRows = data?.rows || [];
  const searchRows = q
    ? allRows.filter((r) =>
        (r.reference || "").toLowerCase().includes(q) ||
        (r.description || "").toLowerCase().includes(q))
    : allRows;
  // On-screen type filter (All / a single type). Server data is unchanged; exports still cover everything shown.
  // Always offer the three core types; include any other present type (e.g. Depreciation) too.
  const CORE_TYPES = ["PREPAYMENT", "ACCRUAL", "DEFERRED_INCOME"];
  const presentTypes = VALID_TYPES.filter((c) => CORE_TYPES.includes(c) || allRows.some((r) => r.category === c));
  const matchRows = viewType === "ALL" ? searchRows : searchRows.filter((r) => r.category === viewType);
  const sumRows = (rs) => ({
    opening: rs.reduce((a, r) => a + (r.opening || 0), 0),
    closing: rs.reduce((a, r) => a + (r.closing || 0), 0),
    months: months.map((_, i) => rs.reduce((a, r) => a + (r.months?.[i] || 0), 0)),
  });
  const groups = VALID_TYPES.filter(
    (c) => matchRows.some((r) => r.category === c));
  const grandTotal = sumRows(matchRows);
  const leadCount = 2 + ["orig_date", "bs_code", "bs_account", "pl_code", "pl_account"].filter((k) => cols[k]).length;
  const totalCols = leadCount + (cols.orig_amount ? 1 : 0) + 2 + months.length;

  return (
    <Page>
      <PageHeader overline="Scheduled releases" title={lockedType ? `${TYPE_LABELS[lockedType]} · 12-Month Forecast` : "12-Month Release Forecast"}
        subtitle="Scheduled accounting releases (not cash flow): opening balance at the start of the period, the value released to the P&L each month, and the closing balance. Figures for future months are projected from current schedules; past months reflect posted journals.">
        {lockedType && <TypeLock label={TYPE_LABELS[lockedType]} />}
        <button onClick={() => setMode("balance")} data-testid="balance-mode-btn"
          className="flex items-center gap-2 bg-white border border-slate-200 text-navy px-4 py-2.5 rounded-lg text-sm font-medium hover:border-navy hover:bg-navy/5 transition-colors">
          <CalendarBlank size={16} /> Balance at month-end
        </button>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
          <CalendarBlank size={16} className="text-navy/70" />
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="forecast-start-date"
            className="text-sm font-mono text-navy outline-none bg-transparent" />
        </div>
        <button onClick={downloadPdf} disabled={pdfing} data-testid="forecast-pdf-btn"
          className="flex items-center gap-2 bg-navy text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-orange transition-colors duration-200 disabled:opacity-60">
          <FilePdf size={18} /> {pdfing ? "BUILDING…" : "PDF"}
        </button>
        <button onClick={downloadExcel} disabled={exceling} data-testid="forecast-excel-btn"
          className="flex items-center gap-2 bg-white border border-positive/40 text-positive px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-positive hover:text-white transition-colors duration-200 disabled:opacity-60">
          <MicrosoftExcelLogo size={18} /> {exceling ? "BUILDING…" : "Excel"}
        </button>
        <ColumnsToggle columns={FC_COLUMNS} visible={cols} onToggle={toggleCol} />
      </PageHeader>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          {!lockedType && presentTypes.length > 1 && (
            <div className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1" data-testid="forecast-type-filter">
              <button onClick={() => setViewType("ALL")} data-testid="forecast-type-ALL"
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${viewType === "ALL" ? "bg-navy text-white" : "text-navy/70 hover:bg-navy/5"}`}>All</button>
              {presentTypes.map((t) => (
                <button key={t} onClick={() => setViewType(t)} data-testid={`forecast-type-${t}`}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${viewType === t ? "bg-navy text-white" : "text-navy/70 hover:bg-navy/5"}`}>{TYPE_LABELS[t]}</button>
              ))}
            </div>
          )}
          <Link to="/reports" data-testid="forecast-reports-link"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-orangeink hover:text-navy transition-colors px-2">
            <ArrowSquareOut size={16} weight="bold" /> View reports
          </Link>
        </div>
        <div className="relative w-full sm:w-80">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/35" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="forecast-search"
            placeholder="Search reference or description…"
            className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-8 py-1.5 text-xs text-navy placeholder:text-navy/35 focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none transition-colors" />
          {search && (
            <button onClick={() => setSearch("")} data-testid="forecast-search-clear"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-navy/35 hover:text-navy">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-auto max-h-[70vh]" data-testid="forecast-table">
        <table className="w-full text-sm" style={{ minWidth: 1900 }}>
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-50 border-b border-slate-200">
              <Th sticky>Reference</Th>
              <Th>Description</Th>
              {cols.orig_date && <Th>Original Date</Th>}
              {cols.bs_code && <Th>BS Code</Th>}
              {cols.bs_account && <Th>BS Account</Th>}
              {cols.pl_code && <Th>P&L Code</Th>}
              {cols.pl_account && <Th>P&L Account</Th>}
              {cols.orig_amount && <Th right>Original Amt</Th>}
              <Th right>Opening</Th>
              {months.map((m) => <Th key={m.key} right>{m.label}</Th>)}
              <Th right>Closing</Th>
            </tr>
          </thead>
          <tbody>
            {groups.map((cat) => {
              const g = matchRows.filter((r) => r.category === cat);
              const sub = sumRows(g);
              return (
                <FragmentGroup key={cat}>
                  <tr className="bg-navy text-white">
                    <td className="px-4 py-2 font-semibold sticky left-0 z-[1] bg-navy">{TYPE_LABELS[cat]} ({g.length})</td>
                    <td colSpan={totalCols - 1}></td>
                  </tr>
                  {g.map((r) => (
                    <tr key={r.schedule_id} data-testid={`forecast-row-${r.schedule_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <Td sticky className="font-medium text-navy bg-white">{r.reference}</Td>
                      <Td className="text-navy/70 max-w-[260px] truncate" title={r.description}>{r.description || "—"}</Td>
                      {cols.orig_date && <Td mono className="text-navy/70 text-xs">{r.original_date ? fmtDate(r.original_date) : "—"}</Td>}
                      {cols.bs_code && <Td mono className="text-navy/70 text-xs">{r.source_account_code || "—"}</Td>}
                      {cols.bs_account && <Td className="text-navy/70 text-xs max-w-[160px] truncate" title={r.source_account_name}>{r.source_account_name || "—"}</Td>}
                      {cols.pl_code && <Td mono className="text-navy/70 text-xs">{r.pl_account_code || "—"}</Td>}
                      {cols.pl_account && <Td className="text-navy/70 text-xs max-w-[160px] truncate" title={r.pl_account_name}>{r.pl_account_name || "—"}</Td>}
                      {cols.orig_amount && <Td right mono className="text-navy/70">{money(r.original_amount, ccy)}</Td>}
                      <Td right mono className="font-semibold">{money(r.opening, ccy)}</Td>
                      {(() => { const rmax = rowMonthMax(r.months); return r.months.map((v, i) => <MonthBarCell key={i} value={v} rowMax={rmax} ccy={ccy} />); })()}
                      <Td right mono className="font-semibold">{money(r.closing, ccy)}</Td>
                    </tr>
                  ))}
                  <tr className="bg-navy/[0.05] font-semibold border-b border-slate-200">
                    <Td sticky className="text-navy bg-[#eef1f5]">Subtotal · {TYPE_LABELS[cat]}</Td>
                    <Td colSpan={leadCount - 1}></Td>
                    {cols.orig_amount && <Td right mono className="text-navy/70">—</Td>}
                    <Td right mono>{money(sub.opening, ccy)}</Td>
                    {(() => { const smax = rowMonthMax(sub.months); return sub.months.map((v, i) => <MonthBarCell key={i} value={v} rowMax={smax} ccy={ccy} strong />); })()}
                    <Td right mono>{money(sub.closing, ccy)}</Td>
                  </tr>
                </FragmentGroup>
              );
            })}
            {matchRows.length > 0 && (
              <tr className="bg-orange text-white font-bold">
                <td className="px-4 py-3 sticky left-0 z-[1] bg-orange">GRAND TOTAL</td>
                <td className="px-4 py-3" colSpan={leadCount - 1}></td>
                {cols.orig_amount && <td className="px-4 py-3 text-right font-mono">—</td>}
                <td className="px-4 py-3 text-right font-mono">{money(grandTotal.opening, ccy)}</td>
                {grandTotal.months.map((v, i) => <td key={i} className="px-4 py-3 text-right font-mono">{money(v, ccy)}</td>)}
                <td className="px-4 py-3 text-right font-mono">{money(grandTotal.closing, ccy)}</td>
              </tr>
            )}
          </tbody>
        </table>
        {!loading && matchRows.length === 0 && (
          <div className="py-16 text-center text-navy/70" data-testid="forecast-empty">
            {allRows.length === 0 ? (
              <>
                <div className="font-heading font-bold text-lg text-navy">Nothing to forecast</div>
                <p className="text-sm mt-1">No schedules have a balance in the 12 months from this date.</p>
              </>
            ) : viewType !== "ALL" && searchRows.some((r) => r.category === viewType) === false ? (
              <>
                <div className="font-heading font-bold text-lg text-navy">No {TYPE_LABELS[viewType]} to forecast</div>
                <p className="text-sm mt-1">No {TYPE_LABELS[viewType].toLowerCase()} have a balance in these 12 months. Try “All” or another type.</p>
              </>
            ) : (
              <>
                <div className="font-heading font-bold text-lg text-navy">No matching items</div>
                <p className="text-sm mt-1">Try a different search term.</p>
              </>
            )}
          </div>
        )}
        {loading && <div className="py-16 text-center font-mono text-slate-600 text-sm tracking-widest">LOADING_DATA…</div>}
      </div>

      <PLMovementForecast rows={(data?.pl_movement || []).filter((r) => viewType === "ALL" || r.category === viewType)}
        months={months} ccy={ccy} start={start} />
    </Page>
  );
}

function PLMovementForecast({ rows, months, ccy, start }) {
  const navigate = useNavigate();
  const [drillCode, setDrillCode] = useState(null);
  const [drill, setDrill] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const openDrill = async (code) => {
    setDrillCode(code); setDrill(null); setDrillLoading(true);
    try {
      const { data } = await api.get(`/forecast/pl-code-schedules?code=${encodeURIComponent(code)}&start=${start}`);
      setDrill(data);
    } catch { toast.error("Could not load schedules for this code"); }
    finally { setDrillLoading(false); }
  };

  if (!rows.length) return null;
  const totalsByMonth = months.map((_, i) => rows.reduce((a, r) => a + (r.months?.[i] || 0), 0));
  const grand = rows.reduce((a, r) => a + (r.total || 0), 0);
  return (
    <div className="mt-8" data-testid="forecast-pl-movement">
      <div className="mb-3">
        <h2 className="font-heading font-bold text-navy text-lg">Releases by P&amp;L code</h2>
        <p className="text-sm text-navy/60">The 12-month releases above, grouped by destination P&amp;L code (split-aware). Click a row to see the schedules driving it.</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-auto max-h-[60vh]">
        <table className="w-full text-sm" style={{ minWidth: 1400 }}>
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-50 border-b border-slate-200">
              <Th sticky>P&L Code</Th><Th>Type</Th>
              {months.map((m) => <Th key={m.key} right>{m.label}</Th>)}
              <Th right>Total</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.pl_account_code} data-testid={`forecast-pl-row-${r.pl_account_code}`}
                onClick={() => openDrill(r.pl_account_code)}
                className="border-b border-slate-100 hover:bg-orange/[0.04] cursor-pointer">
                <Td sticky className="font-medium text-navy bg-white">
                  <span className="inline-flex items-center gap-1.5">
                    <CaretRight size={12} weight="bold" className="text-navy/30" />
                    {r.pl_account_code}{r.pl_account_name ? ` — ${r.pl_account_name}` : ""}
                  </span>
                </Td>
                <Td className="text-navy/70 text-xs">{TYPE_LABELS[r.category] || r.category}</Td>
                {(() => { const rmax = rowMonthMax(r.months); return r.months.map((v, i) => <MonthBarCell key={i} value={v} rowMax={rmax} ccy={ccy} />); })()}
                <Td right mono className="font-semibold">{money(r.total, ccy)}</Td>
              </tr>
            ))}
            <tr className="bg-orange text-white font-bold">
              <td className="px-4 py-3 sticky left-0 z-[1] bg-orange">TOTAL</td>
              <td className="px-4 py-3"></td>
              {totalsByMonth.map((v, i) => <td key={i} className="px-4 py-3 text-right font-mono">{money(v, ccy)}</td>)}
              <td className="px-4 py-3 text-right font-mono">{money(grand, ccy)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {drillCode && (
        <div className="fixed inset-0 z-40 flex justify-end" data-testid="forecast-pl-drill">
          <div className="absolute inset-0 bg-navy/30 backdrop-blur-sm" onClick={() => setDrillCode(null)} />
          <div className="relative w-full max-w-lg bg-white h-full shadow-2xl flex flex-col">
            <div className="flex items-start justify-between px-6 py-5 border-b border-slate-100">
              <div>
                <div className="overline text-navy/60">Future releases to</div>
                <div className="font-heading font-bold text-navy text-lg">{drillCode}{drill?.pl_account_name ? ` — ${drill.pl_account_name}` : ""}</div>
                {drill && <div className="text-sm text-navy/65 mt-1">{drill.schedules.length} schedule(s) · {money(drill.total, drill.currency)} over the next 12 months</div>}
              </div>
              <button onClick={() => setDrillCode(null)} data-testid="forecast-pl-drill-close" className="text-slate-500 hover:text-navy"><X size={22} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {drillLoading && <div className="py-16 text-center font-mono text-navy/60 text-sm tracking-widest">LOADING…</div>}
              {drill && drill.schedules.length === 0 && (
                <div className="py-16 text-center text-navy/60 text-sm" data-testid="forecast-pl-drill-empty">No future releases to this code in this window.</div>
              )}
              {drill && drill.schedules.map((s) => (
                <button key={s.schedule_id} data-testid={`forecast-pl-drill-schedule-${s.schedule_id}`}
                  onClick={() => navigate(`/register?open=${s.schedule_id}`)}
                  className="w-full text-left border border-slate-200 rounded-xl px-4 py-3 mb-2.5 hover:border-orange hover:bg-orange/[0.03] transition-colors">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-navy truncate flex items-center gap-2">
                        {s.reference || "—"}
                        {s.is_split && <span className="text-[0.6rem] font-semibold uppercase px-1.5 py-0.5 rounded-md bg-orange/10 text-orangeink border border-orange/30">Split</span>}
                        {s.manual && <span className="text-[0.6rem] font-semibold uppercase px-1.5 py-0.5 rounded-md bg-navy/[0.06] text-navy/60">Manual</span>}
                      </div>
                      <div className="text-xs text-navy/60 truncate">{s.description || TYPE_LABELS[s.category] || s.category} · from {s.source_account_code}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono font-semibold text-navy">{money(s.future_to_code, drill.currency)}</div>
                      <CaretRight size={14} weight="bold" className="text-navy/25 inline" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FragmentGroup({ children }) { return <>{children}</>; }

const Th = ({ children, right, sticky }) =>
  <th className={`px-4 py-3 ${right ? "text-right" : "text-left"} font-semibold whitespace-nowrap text-[0.66rem] uppercase tracking-wider text-slate-600 bg-slate-50 ${sticky ? "sticky left-0 z-20 min-w-[150px]" : ""}`}>{children}</th>;
const Td = ({ children, right, mono, colSpan, sticky, className = "" }) =>
  <td colSpan={colSpan} className={`px-4 py-3 ${right ? "text-right" : "text-left"} ${mono ? "font-mono tabular" : ""} ${sticky ? "sticky left-0 z-[1] min-w-[150px]" : ""} ${className}`}>{children}</td>;

// Monthly cell with a subtle data bar behind the figure (bar width ∝ value within its row).
function MonthBarCell({ value, rowMax, ccy, strong }) {
  const pct = value ? Math.min(100, (Math.abs(value) / rowMax) * 100) : 0;
  return (
    <td className="relative px-4 py-3 text-right font-mono tabular">
      {pct > 0 && (
        <span aria-hidden className="absolute top-1.5 bottom-1.5 right-1 rounded pointer-events-none"
          style={{ width: `${pct}%`, background: strong ? "rgba(46,163,253,0.24)" : "rgba(46,163,253,0.14)" }} />
      )}
      <span className={`relative ${value ? "text-navy" : "text-navy/25"}`}>{money(value, ccy)}</span>
    </td>
  );
}
const rowMonthMax = (arr) => Math.max(1, ...arr.map((x) => Math.abs(x || 0)));


function BalanceAtView({ onBack, lockedType }) {
  const typeQ = lockedType ? `&type=${lockedType}` : "";
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfing, setPdfing] = useState(false);
  const [exceling, setExceling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/forecast/balance-at?month=${month}-01${typeQ}`);
      setData(data);
    } finally { setLoading(false); }
  }, [month, typeQ]);
  useEffect(() => { load(); }, [load]);

  const dl = async (kind) => {
    const set = kind === "pdf" ? setPdfing : setExceling;
    set(true);
    try {
      const res = await api.get(`/forecast/balance-at/${kind}?month=${month}-01${typeQ}`, { responseType: "blob" });
      const type = kind === "pdf" ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `balance-as-at-${data?.as_at || month}.${kind === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Balance report ${kind === "pdf" ? "PDF" : "Excel"} downloaded`);
    } catch {
      toast.error("Could not generate report");
    } finally { set(false); }
  };

  const ccy = data?.currency || "GBP";
  const rows = data?.rows || [];
  const groups = VALID_TYPES.filter((c) => rows.some((r) => r.category === c));
  const tot = data?.total || {};

  return (
    <Page>
      <PageHeader overline="Scheduled balances" title={lockedType ? `${TYPE_LABELS[lockedType]} · Balance as at Month-End` : "Balance as at Month-End"}
        subtitle="Projected remaining balance for each item at the end of the month you choose, based on current schedules (not cash flow).">
        {lockedType && <TypeLock label={TYPE_LABELS[lockedType]} />}
        <button onClick={onBack} data-testid="back-to-forecast-btn"
          className="flex items-center gap-2 bg-white border border-slate-200 text-navy px-4 py-2.5 rounded-lg text-sm font-medium hover:border-navy hover:bg-navy/5 transition-colors">
          12-Month Forecast
        </button>
        <div className="flex items-center gap-2">
          <CalendarBlank size={16} className="text-navy/70" />
          <MonthPicker value={month} onChange={setMonth} yearsBack={3} yearsForward={6} testId="balance-month" />
        </div>
        <button onClick={() => dl("pdf")} disabled={pdfing} data-testid="balance-pdf-btn"
          className="flex items-center gap-2 bg-navy text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-orange transition-colors duration-200 disabled:opacity-60">
          <FilePdf size={18} /> {pdfing ? "BUILDING…" : "PDF"}
        </button>
        <button onClick={() => dl("excel")} disabled={exceling} data-testid="balance-excel-btn"
          className="flex items-center gap-2 bg-white border border-positive/40 text-positive px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-positive hover:text-white transition-colors duration-200 disabled:opacity-60">
          <MicrosoftExcelLogo size={18} /> {exceling ? "BUILDING…" : "Excel"}
        </button>
      </PageHeader>

      <div className="flex items-center gap-1.5 -mt-2 mb-6 text-sm text-navy/70" data-testid="balance-as-at-label">
        <CalendarBlank size={15} className="text-slate-600" />
        Balance as at <span className="font-medium text-navy/75">{data?.month_label ? `end of ${data.month_label}` : "…"}</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-x-auto" data-testid="balance-table">
        <table className="w-full text-sm" style={{ minWidth: 1100 }}>
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-50 border-b border-slate-200">
              <Th>Reference</Th><Th>Description</Th><Th>BS Code</Th><Th>BS Account</Th>
              <Th>P&L Code</Th><Th>P&L Account</Th>
              <Th right>Original</Th><Th right>Released</Th><Th right>Balance</Th>
            </tr>
          </thead>
          <tbody>
            {groups.map((cat) => {
              const g = rows.filter((r) => r.category === cat);
              const sc = (data.by_category || {})[cat] || {};
              return (
                <FragmentGroup key={cat}>
                  <tr className="bg-navy text-white">
                    <td className="px-4 py-2 font-semibold">{TYPE_LABELS[cat]} ({g.length})</td>
                    <td colSpan={8}></td>
                  </tr>
                  {g.map((r) => (
                    <tr key={r.schedule_id} data-testid={`balance-row-${r.schedule_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <Td className="font-medium text-navy">{r.reference}</Td>
                      <Td className="text-navy/70 max-w-[260px] truncate" title={r.description}>{r.description || "—"}</Td>
                      <Td mono className="text-navy/70 text-xs">{r.source_account_code || "—"}</Td>
                      <Td className="text-navy/70 text-xs max-w-[160px] truncate" title={r.source_account_name}>{r.source_account_name || "—"}</Td>
                      <Td mono className="text-navy/70 text-xs">{r.pl_account_code || "—"}</Td>
                      <Td className="text-navy/70 text-xs max-w-[160px] truncate" title={r.pl_account_name}>{r.pl_account_name || "—"}</Td>
                      <Td right mono className="text-navy/70">{money(r.original_amount, ccy)}</Td>
                      <Td right mono className="text-positive">{money(r.released, ccy)}</Td>
                      <Td right mono className="font-semibold">{money(r.balance, ccy)}</Td>
                    </tr>
                  ))}
                  <tr className="bg-navy/[0.05] font-semibold border-b border-slate-200">
                    <Td colSpan={6} className="text-navy">Subtotal · {TYPE_LABELS[cat]}</Td>
                    <Td right mono>{money(sc.original, ccy)}</Td>
                    <Td right mono>{money(sc.released, ccy)}</Td>
                    <Td right mono>{money(sc.balance, ccy)}</Td>
                  </tr>
                </FragmentGroup>
              );
            })}
            {rows.length > 0 && (
              <tr className="bg-orange text-white font-bold">
                <td className="px-4 py-3" colSpan={6}>GRAND TOTAL</td>
                <td className="px-4 py-3 text-right font-mono">{money(tot.original, ccy)}</td>
                <td className="px-4 py-3 text-right font-mono">{money(tot.released, ccy)}</td>
                <td className="px-4 py-3 text-right font-mono">{money(tot.balance, ccy)}</td>
              </tr>
            )}
          </tbody>
        </table>
        {!loading && rows.length === 0 && (
          <div className="py-16 text-center text-navy/70" data-testid="balance-empty">
            <div className="font-heading font-bold text-lg text-navy">No balances at this date</div>
            <p className="text-sm mt-1">No items have a remaining balance at the end of {data?.month_label || "this month"}.</p>
          </div>
        )}
        {loading && <div className="py-16 text-center font-mono text-slate-600 text-sm tracking-widest">LOADING_DATA…</div>}
      </div>
    </Page>
  );
}
